<template>
  <v-container class="px-5">
    <v-row>
      <v-col cols="12"
             lg="9">
        <h5>{{ $t('bill.header') }}</h5>
        <p>{{ $t('bill.desc') }}</p>
      </v-col>
      <v-col cols="12"
             lg="9">
        <v-row v-for="(bill, i) in bills"
               :id="'bill-' + (i * 1 + 1)"
               :key="i">
          <v-col cols="12">
            <h5>
              {{ $t('common.bill') }} # {{ (i * 1 + 1) }} <v-btn v-if="bills.length > 1"
                                                                 text
                                                                 icon
                                                                 fab
                                                                 class="error--text"
                                                                 style="vertical-align: baseline;"
                                                                 @click="$root.removeBill(i)">
                <v-icon>fa-trash-alt</v-icon>
              </v-btn>
            </h5>
          </v-col>
          <v-col cols="12"
                 sm="6">
            <custom-input-group :model="bill.amount"
                                :validator="$root.$v.order.bills.$each[i].amount"
                                label="amount"
                                @update:model="$root.updateBill('amount', $event.replace(',', '.'), i)" />
          </v-col>
          <v-col cols="12"
                 sm="6">
            <custom-input-group :model="bill.billNr"
                                :validator="$root.$v.order.bills.$each[i].billNr"
                                :maxlength="21"
                                label="billNr"
                                :asterix="bill.information === ''"
                                @update:model="$root.updateBill('billNr', $event, i)" />
          </v-col>
          <v-col cols="12"
                 sm="6">
            <custom-input-group :model="bill.information"
                                :validator="$root.$v.order.bills.$each[i].information"
                                :maxlength="31"
                                label="information"
                                :asterix="bill.billNr === '' || $root.order.basicInformation.claimType === 14"
                                @update:model="$root.updateBill('information', $event, i)" />
          </v-col>
          <v-col cols="12"
                 sm="6">
            <custom-input-group :model="bill.referenceNr"
                                :validator="$root.$v.order.bills.$each[i].referenceNr"
                                :maxlength="21"
                                label="referenceNr"
                                @update:model="$root.updateBill('referenceNr', $event, i)" />
          </v-col>
          <v-col cols="12"
                 sm="6">
            <label class="input-label">{{ $t('labels.billDate') }}</label>
            <v-dialog v-model.trim="bill.billDateModal" width="290px">
              <template v-slot:activator="{ on }">
                <form-group name="billDate"
                            :validator="$root.$v.order.bills.$each[i].billDate">
                  <template slot-scope="{ attrs }">
                    <v-text-field :value="bill.billDate | formatDate"
                                  append-icon="fa-calendar-alt"
                                  readonly
                                  v-bind="attrs"
                                  v-on="on" />
                  </template>
                </form-group>
              </template>
              <v-date-picker v-model.trim="bill.billDate"
                             :locale="longLocale"
                             :max="bill.dueDate !== '' ? bill.dueDate : new Date().toISOString().slice(0,10)"
                             scrollable
                             @change="$root.updateBill('billDate', bill.billDate, i); $root.$v.order.bills.$each[i].billDate.$touch()"
                             @input="bill.billDateModal = false; $root.$v.order.bills.$each[i].billDate.$touch()"
                             @blur="$root.$v.order.bills.$each[i].billDate.$touch()" />
            </v-dialog>
          </v-col>
          <v-col cols="12"
                 sm="6">
            <v-dialog v-model.trim="bill.dueDateModal" width="290px">
              <template v-slot:activator="{ on }">
                <div class="input-group">
                  <label class="input-label">{{ $t('labels.dueDate') }} *</label>
                  <form-group name="dueDate"
                              :validator="$root.$v.order.bills.$each[i].dueDate">
                    <template slot-scope="{ attrs }">
                      <v-text-field :value="bill.dueDate | formatDate"
                                    append-icon="fa-calendar-alt"
                                    v-bind="attrs"
                                    readonly
                                    v-on="on" />
                    </template>
                  </form-group>
                </div>
              </template>
              <v-date-picker v-model.trim="bill.dueDate"
                             :locale="longLocale"
                             :max="new Date().toISOString().slice(0,10)"
                             :min="bill.billDate !== '' ? bill.billDate : ''"
                             scrollable
                             @change="$root.updateBill('dueDate', bill.dueDate, i); $root.$v.order.bills.$each[i].dueDate.$touch()"
                             @input="bill.dueDateModal = false; $root.$v.order.bills.$each[i].dueDate.$touch()"
                             @blur="$root.$v.order.bills.$each[i].dueDate.$touch()" />
            </v-dialog>
          </v-col>
          <v-col cols="12">
            <label class="input-label">{{ $t('labels.interestType') }} *</label>
            <v-radio-group v-model="bill.interestType"
                           :row="$vuetify.breakpoint.lgAndUp"
                           :error="$root.$v.order.bills.$each[i].interestType.$error"
                           :error-messages="$root.$v.order.bills.$each[i].interestType.$error && !$root.$v.order.bills.$each[i].interestType.required ? [$t('validationMessages.required')] : []"
                           class="bill-interest-radiogroup"
                           @change="$root.updateBill('interestType', bill.interestType, i)">
              <v-radio :label="$t('labels.interest.0')"
                       :value="0"
                       @change="$root.$v.order.bills.$each[i].interestType.$touch()" />
              <v-radio :label="$t('labels.interest.2')"
                       :value="2"
                       @change="$root.$v.order.bills.$each[i].interestType.$touch()" />
              <v-radio v-if="mainDebtor.isCompany"
                       :label="$t('labels.interest.1')"
                       :value="1"
                       :disabled="!mainDebtor.isCompany"
                       class="shrink"
                       @change="$root.$v.order.bills.$each[i].interestType.$touch(); $root.$v.order.bills.$each[i].interest.$touch()" />
              <custom-input-group v-if="mainDebtor.isCompany"
                                  :model="bill.interest"
                                  :validator="$root.$v.order.bills.$each[i].interest"
                                  :maxlength="5"
                                  label="interest.1"
                                  :asterix="true"
                                  :disabled="bill.interestType !== 1"
                                  icon="fa-percent"
                                  :showLabel="false"
                                  @update:model="$root.updateBill('interest', $event.replace(',', '.'), i)" />
            </v-radio-group>
          </v-col>
        </v-row>
      </v-col>
      <v-col cols="12"
             class="text-center">
        <v-btn text
               large
               class="primary--text"
               @click="$root.addNewBill">
          <v-icon class="mr-3">
            fa-plus
          </v-icon>
          <span>{{ $t('bill.addBill') }}</span>
        </v-btn>
        <v-tooltip bottom max-width="300px">
          <template v-slot:activator="{ on }">
            <v-icon color="primary" dark v-on="on">fas fa-info-circle</v-icon>
          </template>
          <span>{{ $t('bill.addBillTooltip') }}</span>
        </v-tooltip>
      </v-col>
      <v-row>
        <v-col class="text-center"
               cols="12">
          <v-btn large
                 tile
                 :disabled="!dirtyBills || billError"
                 :class="{ primary: dirtyBills && !billError }"
                 to="/basic-information">
            {{ $t('common.continue') }}
          </v-btn>
        </v-col>
      </v-row>
    </v-row>
  </v-container>
</template>

<script>
  export default {
    computed: {
      bills() {
        return this.$store.getters.bills
      },
      mainDebtor() {
        return this.$store.getters.debtors[0]
      },
      longLocale() {
        return this.$store.getters.longLocale
      },
      dirtyBills() {
        return this.$store.getters.dirtyBills
      },
      billError() {
        return this.$store.getters.billError
      }
    },
    watch: {
      $route() {
        this.$root.scrollTo(this.$router.currentRoute.hash)
      }
    },
    created() {
      this.$root.scrollTo(this.$router.currentRoute.hash)
    },
    beforeRouteLeave(to, from, next) {
      if (this.$root.$v.order.bills.$anyDirty) {
        this.$root.$v.order.bills.$touch()
      }
      next()
    }
  }
</script>
<style>
  .bill-interest-radiogroup .v-input--radio-group__input {
    align-items: baseline;
  }
</style>
